import debounce from "@/utils/debounce.js";

// Появление заголовков и текста при въезде в экран. Блок проявляется не целиком:
// заголовки — слово за словом, обычный текст — строка за строкой.
//
// Слова заворачиваются в спаны, спаны разбиваются на строки замером offsetTop, и
// каждой строке достаётся своя задержка перехода. Как только блок доиграл, спаны
// разбираются обратно — дальше они не нужны, а страница остаётся с чистой разметкой.
//
// Виджет висит на .page-content и собирает цели сам по списку селекторов ниже:
// иначе атрибут пришлось бы проставлять в каждом шаблоне компонента. Подключён он
// только к главной (rootTemplates/main.tpl): на внутренних страницах сплошной текст
// читают, а не разглядывают, и проявлять его по строке незачем.
//
// Прокрутка на сайте своя (виджет smoothScroll), и контент едет трансформом, но
// IntersectionObserver считает пересечение по итоговой геометрии — трансформ он
// учитывает, так что блок появляется там, где его действительно видно.

// Селекторы блоков, которые проявляем. Свёрнутого ответа в FAQ здесь нет намеренно:
// до раскрытия у него нулевая высота, наблюдатель промолчит, и текст останется
// прозрачным внутри уже открытого вопроса
const TARGETS = [
    ".component-title",
    // Только абзац: этим же классом помечены декоративные картинки в шапке и футере
    "p.component-label",
    ".component-description",
    ".component-note",
    ".component-group-title",
    ".component-trial-eyebrow",
    ".component-trial-title",
    ".component-trial-description",
    ".component-trial-price",
    ".component-tile-eyebrow",
    ".component-tile-title",
    ".component-tile-description",
    ".component-tile-price",
    ".component-tile-note",
    ".component-tile-badge",
    ".component-question-text",
    ".component-scan-text",
    ".component-install-title",
    ".component-form-title",
    ".component-contact",
    // Компонент rich-text: на главной его может добавить редактор
    ".html-block :is(h1, h2, h3, h4, h5, h6, p, li, blockquote)"
].join(", ");

const HEADINGS = "h1, h2, h3, h4, h5, h6";

// Слова разделяют переносимые пробелы. Неразрывный, узкий неразрывный, цифровой и
// нулевой ширины остаются внутри слова: браузер по ним строку не рвёт, и спан, в
// котором они лежат, тоже не порвёт — а вот между двумя спанами перенос случился бы
const WORD_SEPARATOR = /([^\S\u00a0\u202f\u2007\ufeff]+)/;
const ONLY_SEPARATOR = /^[^\S\u00a0\u202f\u2007\ufeff]+$/;

// Задержки в миллисекундах: между строками, между словами заголовка, между
// плитками, въехавшими в экран одним махом, и между блоками текста внутри плитки
const LINE_STEP = 120;
const WORD_STEP = 80;
const UNIT_STEP = 120;
const INNER_STEP = 60;
// Дальше этого номера задержка не растёт — длинный абзац не должен доигрывать,
// когда его уже прочитали
const MAX_LINE_STEPS = 6;
const MAX_WORD_STEPS = 10;
const MAX_UNIT_STEPS = 4;
const MAX_INNER_STEPS = 2;
// Плитки въезжают в экран разом, и без своего шага весь ряд вспыхивает сразу.
// Заголовок, подпись и описание одной плитки — это одна плитка, а не три блока
const UNITS = ".component-tile";
// Длительность перехода из main.scss: по ней считаем, когда разбирать спаны
const DURATION = 1000;
// Ждём чуть дольше самого перехода, чтобы не снять слово на последнем кадре
const CLEANUP_MARGIN = 100;
// Сколько ждём шрифт, прежде чем размечать текст тем, что нарисовано
const FONTS_TIMEOUT = 1500;

export default function revealText(root) {
    // Прямая просьба не анимировать
    if (!window.IntersectionObserver || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        uncover();
        return;
    }

    // Разбираем текст уже настоящим шрифтом: по запасному переносы легли бы иначе,
    // и вся разметка по строкам оказалась бы не про то. Пока ждём, текст спрятан
    // стилем из main.scss, так что подмена шрифта проходит незаметно
    whenFontsReady(function () {
        init(root);
        uncover();
    });
}

function init(root) {
    // Страховка в шапке шаблона уже показала текст — значит скрипт добирался долго,
    // и то, что на экране, успели прочитать. Прятать это обратно ради анимации
    // нельзя: возьмём только то, что за пределами экрана
    const late = !document.documentElement.classList.contains("text-reveal");

    // Цель -> её слова, номера строк и порядок в документе
    const states = new Map();
    // Наблюдаемый элемент -> цели, которые он показывает
    const groups = new Map();

    const targets = [];
    const linesBefore = [];

    root.querySelectorAll(TARGETS).forEach(function (element) {
        // Вложенные цели (абзац внутри списка) считаем один раз — по внешней
        if (element.parentElement && element.parentElement.closest(TARGETS)) {
            return;
        }

        const rect = element.getBoundingClientRect();

        // Скрытое сейчас не проявится никогда: наблюдатель о нём не заговорит,
        // а слова так и останутся прозрачными
        if (!rect.width && !rect.height) {
            return;
        }

        if (late && rect.bottom > 0 && rect.top < window.innerHeight) {
            return;
        }

        // Пока текст не тронут, замеряем, какое слово на какой строке стоит. Потом
        // сверим с тем, что вышло после разбора: слово в спане не переносится внутри
        // себя, и строка вроде «направлений.Из» без пробела после точки сложится
        // иначе. Такие блоки вернём как были — иначе текст дёрнется, когда виджет
        // доиграет и уберёт спаны
        const lines = measureWords(element);

        if (lines) {
            targets.push(element);
            linesBefore.push(lines);
        }
    });

    targets.forEach(function (element, order) {
        const words = splitWords(element);

        element.classList.add("reveal-text");
        states.set(element, {
            words,
            order,
            lines: null,
            unit: element.closest(UNITS) || element,
            byWord: element.closest(HEADINGS) !== null
        });

        // Слайды карусели стоят за пределами экрана, и поодиночке их черёд не
        // настанет — за них отвечает сама карусель
        const proxy = element.closest(".swiper") || element;
        const group = groups.get(proxy);

        if (group) {
            group.push(element);
        } else {
            groups.set(proxy, [element]);
        }
    });

    // Все замеры после всех перестроек: вперемешку с ними каждый offsetTop стоил бы
    // отдельного пересчёта раскладки
    measureAll();

    const broken = [];

    states.forEach(function (state, element) {
        if (!sameLines(linesBefore[state.order], state.lines)) {
            broken.push(element);
        }
    });

    broken.forEach(function (element) {
        unwrap(element);
        states.delete(element);
    });

    if (!states.size) {
        return;
    }

    const observer = new IntersectionObserver(onIntersect, {
        // Строка трогается, когда поднялась над нижним краем окна, а не в момент
        // касания: иначе она доигрывает ещё до того, как на неё посмотрели
        rootMargin: "0px 0px -12% 0px"
    });

    groups.forEach((group, proxy) => observer.observe(proxy));

    // Перенос строк зависит от ширины окна
    window.addEventListener("resize", debounce(measureAll, 150));

    function onIntersect(entries) {
        const shown = [];

        entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
                return;
            }

            observer.unobserve(entry.target);

            (groups.get(entry.target) || []).forEach(function (element) {
                if (states.has(element)) {
                    shown.push(element);
                }
            });
        });

        // Порядок в документе вместо позиции на экране: читать координаты посреди
        // прокрутки — значит заставлять браузер пересчитывать раскладку в кадре
        shown.sort((a, b) => states.get(a).order - states.get(b).order);

        let unit = null;
        let unitIndex = -1;
        let innerIndex = 0;

        shown.forEach(function (element) {
            if (states.get(element).unit === unit) {
                innerIndex++;
            } else {
                unit = states.get(element).unit;
                unitIndex++;
                innerIndex = 0;
            }

            reveal(element, unitIndex, innerIndex);
        });
    }

    function reveal(element, unitIndex, innerIndex) {
        const state = states.get(element);
        const base = Math.min(unitIndex, MAX_UNIT_STEPS) * UNIT_STEP
            + Math.min(innerIndex, MAX_INNER_STEPS) * INNER_STEP;
        const step = state.byWord ? WORD_STEP : LINE_STEP;
        const limit = state.byWord ? MAX_WORD_STEPS : MAX_LINE_STEPS;
        let last = base;

        state.words.forEach(function (word, i) {
            const delay = base + Math.min(state.byWord ? i : state.lines[i], limit) * step;

            word.style.transitionDelay = delay + "ms";
            last = Math.max(last, delay);
        });

        element.classList.add("revealed");
        states.delete(element);

        setTimeout(() => unwrap(element), last + DURATION + CLEANUP_MARGIN);
    }

    function measureAll() {
        states.forEach(function (state) {
            // offsetTop, а не прямоугольник: слово до появления сдвинуто трансформом,
            // и в прямоугольнике этот сдвиг уже сидит
            state.lines = groupLines(
                state.words.map((word) => word.offsetTop),
                state.words.map((word) => word.offsetHeight)
            );
        });
    }
}

// Класс на <html> ставит инлайновый скрипт в шапке шаблона, снимаем его мы: к этому
// моменту слова уже спрятаны своим opacity, и текст можно показывать
function uncover() {
    document.documentElement.classList.remove("text-reveal");
}

// Шрифт может и не приехать — тогда ждать его до последнего значит держать страницу
// пустой из-за оформления
function whenFontsReady(callback) {
    if (!document.fonts) {
        callback();
        return;
    }

    // Заставляем браузер разложить страницу: пока текст не размечен, шрифт ему не
    // нужен — он его и не запрашивает, а document.fonts.ready в таком состоянии
    // отвечает «всё загружено», ещё ничего не начав
    void document.body.offsetHeight;

    let called = false;
    const run = function () {
        if (!called) {
            called = true;
            callback();
        }
    };

    document.fonts.ready.then(run);
    setTimeout(run, FONTS_TIMEOUT);
}

// Слова элемента как отрезки внутри текстовых узлов. Разбор и замер до разбора
// ходят по тексту одинаково, поэтому слова у них совпадают одно к одному
function textNodes(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) {
        if (walker.currentNode.data.trim()) {
            nodes.push(walker.currentNode);
        }
    }

    return nodes;
}

// Номера строк слов, пока текст ещё не тронут. Позицию слова берём диапазоном прямо
// по тексту — так замер честный, без спанов, которые сами меняют переносы
function measureWords(element) {
    const range = document.createRange();
    const tops = [];
    const heights = [];

    textNodes(element).forEach(function (node) {
        let offset = 0;

        node.data.split(WORD_SEPARATOR).forEach(function (part) {
            if (part && !ONLY_SEPARATOR.test(part)) {
                range.setStart(node, offset);
                range.setEnd(node, offset + part.length);

                const rect = range.getBoundingClientRect();

                tops.push(rect.top);
                heights.push(rect.height);
            }

            offset += part.length;
        });
    });

    return tops.length ? groupLines(tops, heights) : null;
}

// Каждое слово — в свой спан, переносимые пробелы между ними остаются текстом
function splitWords(element) {
    const words = [];

    textNodes(element).forEach(function (node) {
        const fragment = document.createDocumentFragment();

        node.data.split(WORD_SEPARATOR).forEach(function (part) {
            if (!part) {
                return;
            }

            if (ONLY_SEPARATOR.test(part)) {
                fragment.append(part);
                return;
            }

            const word = document.createElement("span");

            word.className = "reveal-word";
            word.textContent = part;
            fragment.append(word);
            words.push(word);
        });

        node.replaceWith(fragment);
    });

    return words;
}

// Слова на одной строке стоят на общей базовой линии, но верхом расходятся — тем
// сильнее, чем разнее их кегль, поэтому строку считаем сменившейся только после
// заметного скачка вниз
function groupLines(tops, heights) {
    const tolerance = Math.max(Math.max(...heights), 2) * 0.5;
    let line = 0;
    let lineTop = tops[0];

    return tops.map(function (top) {
        if (top - lineTop > tolerance) {
            line++;
            lineTop = top;
        }

        return line;
    });
}

function sameLines(before, after) {
    return before.length === after.length && before.every((line, i) => line === after[i]);
}

// Спаны нужны только на время анимации: дальше блок живёт обычным текстом
function unwrap(element) {
    element.classList.remove("reveal-text", "revealed");
    element.querySelectorAll(".reveal-word").forEach((word) => word.replaceWith(word.textContent));
    element.normalize();
}
